import React, { useState, useMemo, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { AppModal } from '@/components/ui/modal';
import { CardFormNested } from '@/components/forms/CardFormNested';
import { useCards } from '@/contexts/cards';
import { usePopoverState } from '@/hooks/usePopoverStateManager';
import { Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Card {
  id: string;
  name: string;
  bank: string;
  color?: string;
}

interface TransactionCardFieldEnhancedProps {
  value: string;
  onChange: (value: string) => void;
  cards: Card[];
  error?: string;
  disabled?: boolean;
  className?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  onNestedFormStart?: (formType: string) => void;
  onNestedFormEnd?: () => void;
}

export const TransactionCardFieldEnhanced: React.FC<TransactionCardFieldEnhancedProps> = ({
  value,
  onChange,
  cards,
  error,
  disabled = false,
  className,
  label = "Cartão",
  placeholder = "Selecionar cartão",
  required = false,
  onNestedFormStart,
  onNestedFormEnd
}) => {
  const { addCard } = useCards();
  const [showCardForm, setShowCardForm] = useState(false);
  const [isCreatingCard, setIsCreatingCard] = useState(false);
  const [selectOpen, setSelectOpen] = useState(false);
  const { closeAllPopovers } = usePopoverState();

  // Sort cards by name for consistent display
  const sortedCards = useMemo(() => {
    return [...cards].sort((a, b) => a.name.localeCompare(b.name));
  }, [cards]);

  const selectedCard = cards.find(card => card.id === value);

  const handleOpenCardForm = () => {
    console.log('[TransactionCardFieldEnhanced] Opening card form');
    setShowCardForm(true);
    closeAllPopovers(); // Close any open popovers
    onNestedFormStart?.('card');
  };

  const handleCloseCardForm = () => {
    console.log('[TransactionCardFieldEnhanced] Closing card form');
    setShowCardForm(false);
    onNestedFormEnd?.();
  };

  const handleAddCard = async (cardData: any) => {
    console.log('[TransactionCardFieldEnhanced] Adding new card:', cardData);
    setIsCreatingCard(true);
    
    try {
      if (addCard) {
        const newCard = await addCard(cardData);
        
        // Auto-select the newly created card
        if (newCard?.id) {
          onChange(newCard.id);
          console.log('[TransactionCardFieldEnhanced] Auto-selected new card:', newCard.id);
        }
      }
      
      handleCloseCardForm();
    } catch (error) {
      console.error('[TransactionCardFieldEnhanced] Error adding card:', error);
    } finally {
      setIsCreatingCard(false);
    }
  };

  // Auto-close modal when disabled changes (e.g., parent form closes)
  useEffect(() => {
    if (disabled && showCardForm) {
      handleCloseCardForm();
    }
  }, [disabled, showCardForm]);

  return (
    <>
      <div className={cn("space-y-2", className)}>
        <Label 
          htmlFor="cardId"
          className={cn(
            "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          )}
        >
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
        
        <Select 
          value={value} 
          onValueChange={onChange} 
          disabled={disabled || isCreatingCard}
          required={required}
          open={selectOpen}
          onOpenChange={setSelectOpen}
        >
          <SelectTrigger 
            className={cn(
              "touch-target rounded-xl bg-background",
              error && "border-destructive focus:ring-destructive",
              !selectedCard && "text-muted-foreground"
            )}
            id="cardId"
          >
            <SelectValue placeholder={placeholder}>
              {selectedCard && (
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: selectedCard.color || '#6B7280' }} />
                  <span className="truncate">{selectedCard.name} - {selectedCard.bank}</span>
                </div>
              )}
            </SelectValue>
          </SelectTrigger>
          
          <SelectContent>
            {sortedCards.length === 0 ? (
              <div className="p-2">
                <Button 
                  variant="ghost" 
                  className="w-full justify-start text-left" 
                  onClick={() => {
                    setSelectOpen(false);
                    handleOpenCardForm();
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Criar Novo Cartão
                </Button>
              </div>
            ) : (
              <>
                {sortedCards.map(card => (
                  <SelectItem 
                    key={card.id} 
                    value={card.id}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: card.color || '#6B7280' }} />
                      <span className="truncate">{card.name} - {card.bank}</span>
                    </div>
                  </SelectItem>
                ))}
                <div className="border-t mt-1 pt-1">
                  <Button 
                    variant="ghost" 
                    onClick={() => {
                      setSelectOpen(false);
                      handleOpenCardForm();
                    }} 
                    className="w-full justify-start text-left text-sm"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Criar Novo Cartão
                  </Button>
                </div>
              </>
            )}
          </SelectContent>
        </Select>
        
        {error && (
          <p className="text-sm text-destructive">{error}</p>
        )}
      </div>

      {/* Card creation modal */}
      <AppModal
        open={showCardForm}
        onOpenChange={handleCloseCardForm}
        variant="form"
        size="lg"
        isLoading={isCreatingCard}
        isNested={true}
      >
        <CardFormNested
          onClose={handleCloseCardForm}
          onSubmit={handleAddCard}
        />
      </AppModal>
    </>
  );
};
